/**
 * E2E renderer test probe — locks an encoder's billing flag through the
 * same `locateProxy` + `mutate` path production RTMP start uses, via
 * `globalThis.__irisBillingProbe.lock(deviceId)`.
 *
 * Per `think-in-systems` (ai.knowledge/global): a primitive. E2E cannot
 * flip Stripe/cloud-usage limits, so this writes the same
 * `control.encoder.billingLocked` field the destination card reads.
 * Per `stay-in-scope` (ai.knowledge/global): one method, lock only.
 *
 * Mirrors calibration-probe.ts: same preload-bridge IRIS_E2E_TEST gate
 * (bare `process.env` is empty in this renderer), same install-time
 * assignment, same test-only intent.
 */
import type { Device } from '@studio/models/device';
import { locateProxy } from '#~/agent/index.js';

export type BillingProbeLockReceipt = {
  readonly deviceId: string;
  readonly billingLocked: boolean;
};

/** Minimal proxy surface the lock path touches — keeps the factory unit-testable. */
export interface BillingProbeProxy {
  mutate: (
    set: { control: { encoder: { billingLocked: boolean } } },
    unset: Record<string, never>,
    optimistic: boolean
  ) => Promise<void>;
  state: {
    readonly control?:
      | {
          readonly encoder?:
            | {
                readonly billingLocked?: boolean | undefined;
              }
            | undefined;
        }
      | undefined;
  };
}

interface BillingProbe {
  lock(deviceId: string): Promise<BillingProbeLockReceipt>;
}

// Renderer-process gate. See calibration-probe.ts: bare `process.env` is
// empty here; only the preload bridge carries IRIS_E2E_TEST.
const isE2eEnabled = (): boolean => {
  try {
    return (
      (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
        .window?.electron?.process?.env?.IRIS_E2E_TEST === '1'
    );
  } catch {
    return false;
  }
};

/**
 * Build `lock` against an injected locator so the receipt/throw contract
 * is unit-testable without installing the window global.
 */
export const createBillingLock =
  (locate: (deviceId: string) => BillingProbeProxy | null | undefined) =>
  async (deviceId: string): Promise<BillingProbeLockReceipt> => {
    const proxy = locate(deviceId);
    if (!proxy) {
      throw new Error(`[billing-probe] No proxy found for device ${deviceId}.`);
    }

    await proxy.mutate({ control: { encoder: { billingLocked: true } } }, {}, true);

    // optimisticMutations: local `proxy.state` already reflects the mutate.
    const billingLocked = !!proxy.state.control?.encoder?.billingLocked;
    if (!billingLocked) {
      throw new Error(
        `[billing-probe] Device ${deviceId} is not billing-locked after mutate (observed ${String(
          proxy.state.control?.encoder?.billingLocked
        )}).`
      );
    }

    return { deviceId, billingLocked };
  };

if (isE2eEnabled()) {
  const probe: BillingProbe = {
    lock: createBillingLock((deviceId) => locateProxy(deviceId as Device['id']))
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__irisBillingProbe = probe;
}
